import { useEffect } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Contact from "@/components/Contact";
import Process from "@/components/Process";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { QuoteModal } from "./QuoteModal";
import { Star, ShieldCheck, Clock, Phone } from "lucide-react";
import Partners from "@/components/Partners";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Link, useLocation } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
interface ServiceLayoutProps {
  title: string;
  subline?: string;
  description: string;
  image: string;
  heroBg?: string;
  content: React.ReactNode;
  extendedContent?: React.ReactNode;
}

export default function ServiceLayout({ title, subline = "Für Privat & Gewerbe in der Region", description, image, heroBg, content, extendedContent }: ServiceLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schemaData = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": title,
      "description": description,
      "provider": {
        "@id": "https://easy-umzugsservice.de/#localbusiness"
      },
      "areaServed": {
        "@type": "State",
        "name": "Bayern"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://easy-umzugsservice.de/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": title
        }
      ]
    }
  ];

  const location = useLocation();

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      <SEOHead 
        title={`${title} - Easy Umzug`}
        description={description}
        canonical={location.pathname}
        ogImage={image}
        schemaJson={JSON.stringify(schemaData)}
      />
      <Navigation />
      <main className="pt-[88px]">
        {/* Text-first hero as per rules: No hero images */}
        <section className="py-20 md:py-32 bg-neutral-950 text-white relative overflow-hidden">
          {heroBg && <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url('${heroBg}')` }}></div>}
          <div className="container mx-auto px-4 md:px-6 text-center max-w-4xl relative z-10">
            <div className="flex justify-center mb-8">
              <Breadcrumb>
                <BreadcrumbList className="text-neutral-300">
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to="/" className="hover:text-white">Home</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="text-neutral-400" />
                  <BreadcrumbItem>
                    <BreadcrumbPage className="text-white">{title}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
            <motion.span 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block font-bold tracking-wider text-primary mb-4 uppercase text-sm md:text-base bg-white/10 px-4 py-1.5 rounded-full backdrop-blur-sm"
            >
              {subline}
            </motion.span>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 uppercase tracking-tight text-balance"
            >
              {title}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg md:text-xl text-neutral-300 mb-10 text-balance"
            >
              {description}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <QuoteModal>
                  <Button size="lg" className="w-full sm:w-auto">
                    Kostenloses Angebot anfordern
                  </Button>
                </QuoteModal>
                <Button size="lg" variant="secondary" asChild className="w-full sm:w-auto">
                  <a href="tel:+4915888640762">
                    <Phone className="w-5 h-5 mr-2" />
                    +49 15888 640762
                  </a>
                </Button>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap items-center justify-center gap-6 text-sm md:text-base text-neutral-200 mt-8"
            >
              <a href="https://share.google/xw8JE3IKl3tC9Kcw0" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-white/10 hover:bg-white/20 transition-colors px-4 py-2 rounded-lg backdrop-blur-sm border border-white/10">
                <img src="https://storage.googleapis.com/revex-reputation-production/assets/google-icon.svg" alt="Google" width="24" height="24" loading="lazy" decoding="async" className="w-6 h-6" />
                <div className="text-left">
                  <div className="flex text-yellow-400 gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                  <div className="text-xs font-medium text-white mt-0.5">5.0 / 5.0 (60+ Bewertungen)</div>
                </div>
              </a>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                <span className="font-medium">Voll versichert</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                <span className="font-medium">Schnell & Zuverlässig</span>
              </div>
            </motion.div>
          </div>
        </section>

        <Partners />

        <section className="py-20 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="aspect-square md:aspect-[4/3] relative overflow-hidden group rounded-xl [transform:translateZ(0)] shadow-lg"
              >
                <img 
                  src={image} 
                  alt={title} 
                  width="800"
                  height="600"
                  loading="lazy"
                  decoding="async"
                  className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105"
                />
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                {content}
              </motion.div>
            </div>
          </div>
        </section>
        {extendedContent}
        <Process />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
